import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatNum, formatSgd, apiErr } from "../lib/utils";
import { ProvenanceTrigger } from "../components/provenance/ProvenanceTrigger";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Coins, Sparkles, Scale, Recycle, Leaf, Settings2, ShieldCheck, AlertTriangle, HelpCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = { retired: "success", purchased: "warning", available: "outline" };

function Stat({ label, value, unit, sub, icon: Icon, testid, tone, pkey }) {
  return (
    <div className="card-flat p-4 group" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2"><Icon className="h-4 w-4" /><span className="label-eyebrow text-[10px]">{label}</span>{pkey && <ProvenanceTrigger pkey={pkey} className="ml-auto" />}</div>
      <div className={`font-mono text-2xl font-bold tracking-tight ${tone || ""}`}>{value}<span className="text-xs text-muted-foreground font-sans ml-1">{unit}</span></div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function ICPSettings({ config, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ price_sgd_per_tco2e: config.price_sgd_per_tco2e || 0, escalation_pct_yr: config.escalation_pct_yr || 0, application: config.application || "shadow_price", basis: config.basis || "" });
  const save = async () => {
    try { await api.put("/icp-offset/config", { ...f, price_sgd_per_tco2e: Number(f.price_sgd_per_tco2e), escalation_pct_yr: Number(f.escalation_pct_yr) }); setOpen(false); onSaved(); toast.success("Internal carbon price updated"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="icp-edit-btn"><Settings2 className="h-4 w-4" /> Edit ICP</Button></DialogTrigger>
      <DialogContent data-testid="icp-edit-dialog">
        <DialogHeader><DialogTitle className="font-display">Internal carbon price assumptions</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label-eyebrow text-[10px]">Price (S$/tCO₂e)</label><Input type="number" value={f.price_sgd_per_tco2e} onChange={(e) => setF({ ...f, price_sgd_per_tco2e: e.target.value })} data-testid="icp-price-input" /></div>
          <div><label className="label-eyebrow text-[10px]">Escalation (%/yr)</label><Input type="number" value={f.escalation_pct_yr} onChange={(e) => setF({ ...f, escalation_pct_yr: e.target.value })} /></div>
          <div className="col-span-2"><label className="label-eyebrow text-[10px]">Basis / rationale</label><Input value={f.basis} onChange={(e) => setF({ ...f, basis: e.target.value })} /></div>
        </div>
        <div className="flex justify-end"><Button variant="primary" onClick={save} data-testid="icp-save-btn">Save</Button></div>
      </DialogContent>
    </Dialog>
  );
}

export default function ICPOffset() {
  const [d, setD] = useState(null);
  const [ai, setAi] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.get("/icp-offset/dashboard").then((r) => setD(r.data)).catch(() => setD({ empty: true })); }, []);
  useEffect(() => { load(); }, [load]);
  const genAi = async () => { setBusy(true); try { const r = await api.post("/icp-offset/ai/icp-summary"); setAi(r.data.summary); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };

  if (!d) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading ICP & offsets…</div>;
  if (d.empty) return <div className="p-8"><div className="card-flat p-12 text-center"><h2 className="font-display text-2xl font-bold">No ICP data yet.</h2></div></div>;

  const cmp = d.comparison, icc = d.icc, off = d.offsets, cfg = d.icp_config;
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="icp-offset-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">Finance & Offset · ICP and Offset</div>
          <p className="text-[11px] text-muted-foreground mb-1" data-testid="icp-plan-note">
            Basis: FY2024 calendar-year emissions, Scopes 1+2. Summary figures are included free on the Finance &amp; Offset tab — this full ICP &amp; Offset workspace is a plan add-on.
          </p>
          <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3"><Coins className="h-8 w-8 text-accent" /> ICP and Offset
            <Link to="/financing/guide#icp-offset" title="Open the module user guide" data-testid="icp-guide-link"
              className="text-muted-foreground/50 hover:text-accent transition-colors">
              <HelpCircle className="h-4 w-4" />
            </Link>
          </h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">Internal carbon price assumptions, shadow-vs-statutory comparison, ICC eligibility, and the carbon-credit registry with usage limits and retirement evidence.</p>
        </div>
        <ICPSettings config={cfg} onSaved={load} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Internal carbon price" value={`S$${cmp.internal_price}`} unit="/t" icon={Coins} testid="icp-stat-price" pkey="icp.price" sub={`+${cfg.escalation_pct_yr || 0}%/yr`} />
        <Stat label="Statutory rate 2026" value={`S$${cmp.statutory_rate_2026}`} unit="/t" icon={Scale} testid="icp-stat-statutory" pkey="icp.statutory" sub={cmp.is_taxable ? "Taxable" : "Below threshold"} />
        <Stat label="Internal charge" value={formatSgd(cmp.internal_annual_charge_sgd)} unit="/yr" icon={Scale} testid="icp-stat-charge" pkey="icp.charge" />
        <Stat label="Credits retired" value={formatNum(off.retired)} unit="tCO₂e" icon={Recycle} testid="icp-stat-retired" pkey="offsets.retired" />
        <Stat label="Offset coverage" value={off.coverage_pct} unit="%" icon={Leaf} pkey="offsets.coverage" sub={`${formatNum(off.residual_after_offsets_tco2e)}t residual`} />
        <Stat label="ICC usage limit" value={icc.applies ? formatNum(icc.usage_limit_tco2e) : "N/A"} unit={icc.applies ? "tCO₂e" : ""} icon={ShieldCheck} testid="icp-stat-icc" pkey="icp.icc_limit" tone={icc.applies && !icc.within_limit ? "text-red-500" : ""} sub={icc.applies ? `${icc.limit_pct}% cap` : "Below threshold"} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="icp-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="registry" data-testid="icp-tab-registry">Offset Registry</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="card-flat p-4 space-y-2">
              <div className="label-eyebrow mb-1 inline-flex items-center gap-1"><Scale className="h-3.5 w-3.5" /> Internal vs statutory price</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Internal carbon price</span><span className="font-mono">S${cmp.internal_price}/t</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Statutory rate (2024)</span><span className="font-mono">S${cmp.statutory_rate_2024}/t</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Statutory rate (2026)</span><span className="font-mono">S${cmp.statutory_rate_2026}/t</span></div>
                <div className="flex justify-between pt-1 border-t border-border"><span className="text-muted-foreground">Internal annual charge</span><span className="font-mono">{formatSgd(cmp.internal_annual_charge_sgd)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Statutory annual charge</span><span className="font-mono">{formatSgd(cmp.statutory_annual_charge_sgd)}</span></div>
              </div>
              <p className="text-[11px] text-muted-foreground pt-1">{cfg.basis}</p>
            </div>
            <div className="card-flat p-4 space-y-3">
              <div className="label-eyebrow mb-1 inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> ICC eligibility & usage limit</div>
              {icc.applies ? (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    {icc.within_limit ? <ShieldCheck className="h-4 w-4 text-[#00E65B]" /> : <AlertTriangle className="h-4 w-4 text-red-500" />}
                    <span>{icc.within_limit ? "Within CPA 5% usage limit" : "ICC-eligible credits exceed the 5% usage cap"}</span>
                  </div>
                  <div className="flex justify-between text-xs mb-1"><span>ICC-eligible credits vs limit</span><span className="font-mono">{formatNum(icc.eligible_credits)} / {formatNum(icc.usage_limit_tco2e)} t</span></div>
                  <Progress value={icc.usage_limit_tco2e ? Math.min(100, (icc.eligible_credits / icc.usage_limit_tco2e) * 100) : 0} className="h-1.5" />
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-sm"><ShieldCheck className="h-4 w-4 text-[#00E65B]" /><span>Voluntary offsetting — no statutory cap applies</span></div>
                  <div className="flex justify-between text-xs"><span className="text-muted-foreground">ICC-eligible credits held</span><span className="font-mono">{formatNum(icc.eligible_credits)} tCO₂e</span></div>
                </>
              )}
              <div className="flex justify-between text-xs pt-1"><span className="text-muted-foreground">Residual after offsets</span><span className="font-mono">{formatNum(off.residual_after_offsets_tco2e)} tCO₂e</span></div>
              <p className="text-[11px] text-muted-foreground">Under the Carbon Pricing Act, eligible international carbon credits may offset up to {icc.limit_pct}% of <strong>taxable</strong> emissions (facilities ≥ 25,000 tCO₂e). Prioritise abatement for residual emissions.</p>
            </div>
          </div>
          <div className="card-flat p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="label-eyebrow inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> AI carbon-pricing summary</div>
              <Button size="sm" variant="primary" onClick={genAi} disabled={busy} data-testid="icp-ai">{busy ? "Generating…" : "Generate"}</Button>
            </div>
            {ai ? <p className="text-sm leading-relaxed" data-testid="icp-ai-text">{ai}</p> : <p className="text-sm text-muted-foreground">Generate an assessment of your internal carbon price vs statutory rates, offset coverage and ICC usage.</p>}
          </div>
        </TabsContent>

        <TabsContent value="registry" className="mt-4">
          <div className="card-flat overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Project</TableHead><TableHead>Registry</TableHead><TableHead>Type</TableHead><TableHead>Vintage</TableHead>
                <TableHead className="text-right">Credits</TableHead><TableHead className="text-right">Cost/credit</TableHead>
                <TableHead>ICC</TableHead><TableHead>Status</TableHead><TableHead>Retirement</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {d.registry.map((o) => (
                  <TableRow key={o.id} data-testid={`icp-registry-row-${o.id}`}>
                    <TableCell className="text-sm font-medium">{o.project_name}<div className="text-[10px] text-muted-foreground">{o.country}</div></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{o.registry}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{o.project_type}</TableCell>
                    <TableCell className="font-mono text-xs">{o.vintage}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{formatNum(o.credits_tco2e)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">S${o.cost_sgd_per_credit}</TableCell>
                    <TableCell>{o.eligible_iccs ? <Badge variant="lime" className="text-[9px]">Eligible</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                    <TableCell><Badge variant={STATUS_TONE[o.status]} className="text-[9px]">{o.status}</Badge></TableCell>
                    <TableCell className="text-[10px] font-mono text-muted-foreground">{o.retirement_serial ? <>{o.retirement_serial}<div>{o.retired_date}</div></> : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
